import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { SaveVehicleDocumentRequest, VehicleDocumentItem } from '@/api/generated/schemas';

const codebookRefSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string().nullable(),
});

/**
 * Příloha ve formuláři. Uložená příloha má serverové `id` a `contentBase64 = null`; nově vybraný
 * soubor je naopak bez `id` a nese obsah, který se odešle až s uložením dokumentu.
 */
const fileSchema = z.object({
  id: z.string().nullable(),
  fileName: z.string(),
  contentType: z.string(),
  contentBase64: z.string().nullable(),
});

/**
 * Factory Zod schématu dokumentu vozu — single source of truth dialogu. Zrcadlí serverovou validaci
 * (`SaveVehicleDocumentValidator`); lokalizované hlášky se vkládají přímo do schématu, proto se staví
 * z `t` až za běhu.
 */
export function buildVehicleDocumentFormSchema(t: TFunction) {
  return z
    .object({
      id: z.number().nullable(),
      documentType: codebookRefSchema.nullable(),
      number: z
        .string()
        .trim()
        .min(1, t('documents.validation.numberRequired'))
        .max(50, t('documents.validation.numberMaxLength')),
      issuedOn: z.string().nullable(),
      validTo: z.string().min(1, t('documents.validation.validToRequired')),
      files: z.array(fileSchema),
    })
    .superRefine((value, ctx) => {
      if (value.documentType == null) {
        ctx.addIssue({
          code: 'custom',
          path: ['documentType'],
          message: t('documents.validation.typeRequired'),
        });
      }
      if (value.issuedOn && value.validTo && value.validTo < value.issuedOn) {
        ctx.addIssue({
          code: 'custom',
          path: ['validTo'],
          message: t('documents.validation.validToBeforeIssued'),
        });
      }
    });
}

export type VehicleDocumentFormValues = z.infer<ReturnType<typeof buildVehicleDocumentFormSchema>>;
export type VehicleDocumentFileValue = VehicleDocumentFormValues['files'][number];

/** ISO datum (`YYYY-MM-DD`) z API hodnoty; prázdná hodnota → `null`. */
function toIsoDate(value: string | null | undefined): string | null {
  return value ? value.slice(0, 10) : null;
}

export function emptyVehicleDocumentFormValues(): VehicleDocumentFormValues {
  return {
    id: null,
    documentType: null,
    number: '',
    issuedOn: null,
    validTo: '',
    files: [],
  };
}

/** Mapuje uložený dokument na hodnoty formuláře (režim editace). */
export function fromVehicleDocumentItem(item: VehicleDocumentItem): VehicleDocumentFormValues {
  return {
    id: item.id,
    documentType: {
      id: item.documentTypeCbId,
      name: item.documentTypeName ?? '',
      code: null,
    },
    number: item.number,
    issuedOn: toIsoDate(item.issuedOn),
    validTo: toIsoDate(item.validTo) ?? '',
    files: item.files.map((file) => ({
      id: file.id,
      fileName: file.fileName,
      contentType: file.contentType,
      contentBase64: null,
    })),
  };
}

/** Sestaví tělo požadavku; `satisfies` hlídá shodu s BE kontraktem. */
export function toVehicleDocumentRequestBody(
  values: VehicleDocumentFormValues
): SaveVehicleDocumentRequest {
  return {
    id: values.id,
    documentTypeCbId: values.documentType!.id,
    number: values.number.trim(),
    issuedOn: values.issuedOn,
    validTo: values.validTo,
    files: values.files.map((file) => ({
      id: file.id,
      fileName: file.fileName,
      contentType: file.contentType,
      contentBase64: file.contentBase64,
    })),
  } satisfies SaveVehicleDocumentRequest;
}
